import { ALL_SOUND_URLS } from './catalog'

export type PlaybackHandle = {
  stop: () => void
}

const noopHandle: PlaybackHandle = { stop() {} }

export class AudioEngine {
  private ctx: AudioContext | null = null
  private master: GainNode | null = null
  private readonly buffers = new Map<string, AudioBuffer>()
  private readonly live = new Set<AudioBufferSourceNode>()
  private raw = new Map<string, ArrayBuffer>()
  private fetchPromise: Promise<void>
  private unlocked = false
  private muted: boolean
  private cueTimer = 0
  private followHandle: PlaybackHandle | null = null

  constructor(muted: boolean) {
    this.muted = muted
    this.fetchPromise = this.fetchAll()
  }

  isUnlocked(): boolean {
    return this.unlocked
  }

  setMuted(muted: boolean): void {
    this.muted = muted
    if (this.master && this.ctx) {
      this.master.gain.setValueAtTime(muted ? 0 : 1, this.ctx.currentTime)
    }
    if (muted) this.stopAll()
  }

  isMuted(): boolean {
    return this.muted
  }

  unlock(): Promise<void> {
    const ctx = this.ensureContext()
    const silent = ctx.createBuffer(1, 1, ctx.sampleRate)
    const src = ctx.createBufferSource()
    src.buffer = silent
    src.connect(ctx.destination)
    src.start(0)
    this.unlocked = true
    const resume = ctx.state === 'running' ? Promise.resolve() : ctx.resume()
    return resume.then(() => this.decodeAll())
  }

  play(id: string, opts?: { loop?: boolean; gain?: number }): PlaybackHandle {
    if (!this.unlocked || this.muted) return noopHandle
    const buffer = this.buffers.get(id)
    const ctx = this.ctx
    const master = this.master
    if (!buffer || !ctx || !master) return noopHandle
    const src = ctx.createBufferSource()
    src.buffer = buffer
    src.loop = opts?.loop ?? false
    const gain = ctx.createGain()
    gain.gain.value = opts?.gain ?? 1
    src.connect(gain)
    gain.connect(master)
    this.live.add(src)
    src.onended = () => {
      this.live.delete(src)
    }
    src.start(0)
    return {
      stop: () => {
        try {
          src.stop()
        } catch {
          // already stopped
        }
        this.live.delete(src)
      },
    }
  }

  durationMs(id: string): number {
    const buffer = this.buffers.get(id)
    if (!buffer) return 0
    return Math.round(buffer.duration * 1000)
  }

  playFeedback(leadId: string, followIds: readonly string[] = []): void {
    this.clearCue()
    this.play(leadId)
    const chain = [...followIds]
    const step = (wait: number) => {
      if (chain.length === 0) return
      this.cueTimer = window.setTimeout(() => {
        const next = chain.shift()
        if (!next) return
        this.followHandle?.stop()
        this.followHandle = this.play(next)
        step(this.durationMs(next) || 160)
      }, wait)
    }
    step(this.durationMs(leadId) || 80)
  }

  stopAll(): void {
    this.clearCue()
    for (const src of [...this.live]) {
      try {
        src.stop()
      } catch {
        // already stopped
      }
      this.live.delete(src)
    }
  }

  private clearCue(): void {
    if (this.cueTimer) {
      window.clearTimeout(this.cueTimer)
      this.cueTimer = 0
    }
    this.followHandle?.stop()
    this.followHandle = null
  }

  private ensureContext(): AudioContext {
    if (this.ctx && this.master) return this.ctx
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    const ctx = new Ctor()
    const master = ctx.createGain()
    master.gain.value = this.muted ? 0 : 1
    master.connect(ctx.destination)
    this.ctx = ctx
    this.master = master
    return ctx
  }

  private async fetchAll(): Promise<void> {
    const entries = await Promise.all(
      ALL_SOUND_URLS.map(async (url) => {
        const res = await fetch(url, { cache: 'force-cache' })
        if (!res.ok) return null
        const data = await res.arrayBuffer()
        return [url, data] as const
      }),
    )
    for (const entry of entries) {
      if (!entry) continue
      this.raw.set(entry[0], entry[1])
    }
  }

  private async decodeAll(): Promise<void> {
    await this.fetchPromise
    const ctx = this.ensureContext()
    const pending: Promise<void>[] = []
    for (const [url, data] of this.raw) {
      if (this.buffers.has(url)) continue
      pending.push(
        ctx.decodeAudioData(data.slice(0)).then((buffer) => {
          this.buffers.set(url, buffer)
        }),
      )
    }
    await Promise.all(pending)
    this.raw.clear()
  }
}
